/**
 * Product page helpers.
 * <pdp-videos> — [data-video-open] thumbnails open the <dialog> with the video kept in the
 *   thumbnail's <template>; closing empties the stage so playback stops.
 * <pdp-upsell> — keeps the progress bar in step with the horizontal scroller.
 */
class PdpVideos extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.querySelector('[data-video-dialog]')?.addEventListener('close', this.#onClose);
  }
  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-video-dialog]'));
    if (!dialog) return;
    const thumb = target.closest('[data-video-open]');
    if (thumb) {
      const template = thumb.querySelector('template');
      const stage = dialog.querySelector('[data-video-stage]');
      if (!template || !stage) return;
      // importNode, not template.content.cloneNode: iOS Safari never starts loading a <video>
      // created in the template's inert document (black player, 00:00), even after insertion.
      stage.replaceChildren(document.importNode(template.content, true));
      dialog.showModal();
      const video = stage.querySelector('video');
      video?.load();
      video?.play().catch(() => {});
      return;
    }
    if (target.closest('[data-video-close]') || target === dialog) dialog.close();
  };
  #onClose = () => {
    this.querySelector('[data-video-stage]')?.replaceChildren();
  };
}

class PdpUpsell extends HTMLElement {
  connectedCallback() {
    const track = this.querySelector('[data-upsell-track]');
    if (!track) return;
    track.addEventListener('scroll', this.#update, { passive: true });
    new ResizeObserver(this.#update).observe(track);
    this.#update();
  }
  #update = () => {
    const track = this.querySelector('[data-upsell-track]');
    const thumb = /** @type {HTMLElement | null} */ (this.querySelector('[data-upsell-thumb]'));
    if (!track || !thumb) return;
    const max = track.scrollWidth - track.clientWidth;
    thumb.style.setProperty('--progress', max > 1 ? String(Math.min(Math.max(track.scrollLeft / max, 0), 1)) : '0');
    this.querySelector('.pdp-upsell__progress')?.toggleAttribute('hidden', max <= 1);
  };
}

if (!customElements.get('pdp-videos')) customElements.define('pdp-videos', PdpVideos);
if (!customElements.get('pdp-upsell')) customElements.define('pdp-upsell', PdpUpsell);
